import * as React from 'react';
import * as SwitchPrimitives from '@radix-ui/react-switch';

import { cn } from 'src/lib/utils';
import { useAppSelector } from 'src/store';
import { ProductsType } from 'src/constants/products';
import { useSearchParams } from 'react-router-dom';

type SwitchProps = Omit<React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>, 'type'> & {
  type?: string;
};

const Switch = React.forwardRef<React.ComponentRef<typeof SwitchPrimitives.Root>, SwitchProps>(
  ({ className, type = '', ...props }, ref) => {
    const [searchParams] = useSearchParams();
    const searchType = searchParams.get('type');
    const { projectType } = useAppSelector((s) => s.projectsSlices);
    const projectTypeVar = projectType || searchType;

    return (
      <SwitchPrimitives.Root
        className={cn(
          'peer inline-flex h-4 w-11 md:h-4 md:w-8 p-0.5 shrink-0 cursor-pointer items-center rounded-full border border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=unchecked]:bg-ebony data-[state=unchecked]:bg-opacity-20 data-[state=unchecked]:border-[#B8C1C666]',
          className,
          projectTypeVar === ProductsType.agent &&
            'data-[state=checked]:bg-[#75F3FB] data-[state=checked]:border-[#6DD9E0]',
          projectTypeVar === ProductsType.customer &&
            'data-[state=checked]:bg-[#A498FF] data-[state=checked]:border-[#9D87F3]',
          projectTypeVar === ProductsType.bi &&
            'data-[state=checked]:bg-[#E8FD65] data-[state=checked]:border-[#DFF800]',
          !projectTypeVar &&
            'data-[state=checked]:bg-[#75F3FB] data-[state=checked]:border-[#6DD9E0]',
          type === 'green' &&
            'data-[state=checked]:bg-[#33FF71] data-[state=checked]:border-[#35BC5A66]',
          type === 'accent' &&
            'data-[state=checked]:bg-green_750 data-[state=checked]:border-green_750 data-[state=unchecked]:bg-grey_300 data-[state=unchecked]:border-grey_300 data-[state=unchecked]:bg-opacity-100',
          type === 'black' &&
            'data-[state=checked]:bg-ebony_opacity_80 data-[state=checked]:border-ebony_opacity_80 data-[state=unchecked]:bg-light data-[state=unchecked]:border-[#d9dedf] data-[state=unchecked]:bg-opacity-100',
        )}
        {...props}
        ref={ref}
      >
        <SwitchPrimitives.Thumb
          className={cn(
            'pointer-events-none block h-3 w-3 rounded-full data-[state=unchecked]:bg-white drop-shadow bg-background shadow-lg ring-0 transition-transform md:data-[state=checked]:translate-x-3.5 data-[state=checked]:translate-x-3.5 data-[state=unchecked]:translate-x-0',
          )}
        />
      </SwitchPrimitives.Root>
    );
  },
);
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
